import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, type TipoUsuario } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedTypes?: TipoUsuario[];
}

/**
 * Returns the dashboard path for a given user type.
 */
function getDashboardPath(tipo: TipoUsuario): string {
  switch (tipo) {
    case 'Cliente':
      return '/empreendimentos';
    case 'Arquiteto':
      return '/painel';
    case 'Assistente':
      return '/admin';
    case 'Master':
      return '/admin/conteudo';
  }
}

/**
 * A route guard component that:
 * 1. Shows a loading state while auth is being checked
 * 2. Redirects to / if user is not authenticated
 * 3. Redirects to /trocar-senha if user needs to change provisional password
 * 4. Redirects to the appropriate dashboard if user type is not in allowedTypes
 */
export default function ProtectedRoute({
  children,
  allowedTypes,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: '#c9a96e',
          fontSize: '1.2rem',
        }}
      >
        Carregando...
      </div>
    );
  }

  // Redirect to main site if not authenticated
  if (!isAuthenticated || !user) {
    const isAppDomain = window.location.hostname === 'app.vavassori.arq.br';
    if (isAppDomain) {
      window.location.href = 'https://vavassori.arq.br';
      return null;
    }
    return <Navigate to="/" replace />;
  }

  // Redirect to troca de senha se senha provisória (exceto se já está nessa página ou em alterar-senha)
  if (user.requerTrocaSenha && location.pathname !== '/trocar-senha' && location.pathname !== '/alterar-senha') {
    return <Navigate to="/trocar-senha" replace />;
  }

  // Master é superusuário — acesso a todas as rotas
  if (user.tipo === 'Master') {
    return <>{children}</>;
  }

  // Redirect to appropriate dashboard if type not allowed
  if (allowedTypes && !allowedTypes.includes(user.tipo)) {
    return <Navigate to={getDashboardPath(user.tipo)} replace />;
  }

  return <>{children}</>;
}

export { getDashboardPath };
